import {
  Analytics,
  Assignment,
  CompareArrows,
  Dashboard,
  Description,
  Factory,
  FactCheck,
  LockOutlined,
  Science,
  Settings,
  ShowChart,
  Storage,
  Store,
  VisibilityOutlined,
  Warehouse,
} from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@/hooks/redux'
import { useGetMeQuery } from '@/store/api/authApi'
import { PageDataState } from '@/components/PageDataState'

interface QuickLink {
  title: string
  subtitle: string
  path: string
  icon: typeof Analytics
  color: string
  /** Function key trong /settings/roles — quyết định style tile (Edit thường / View / NoGrant). */
  permKey: string
}

// Đồng bộ màu trạng thái quyền với sidebar (Layout VIEW_COLOR / NOGRANT_COLOR).
// View dùng xám dịu để "chìm" hơn Edit; chỉ dấu hiệu (icon mắt + chip) là teal nhạt.
const VIEW_COLOR = '#94a29e'
const NOGRANT_COLOR = '#a13a3a'
// Card Edit dùng 1 accent chung (teal TLC) — nhiều màu theo module nhìn rối.
const EDIT_ACCENT = '#145a59'

// 1:1 với danh sách function tại /settings/roles (SettingsRoleDialog PERMISSION_ROWS).
// Mỗi function permission = 1 Quick Access tile; account nào có quyền (Edit/View) function đó mới hiện tile.
const quickLinks: QuickLink[] = [
  {
    title: 'Report',
    subtitle: 'P&L, Risk, Long/Short, Cash Flows',
    path: '/report/dashboards/pnl',
    icon: Analytics,
    color: '#4a3d8f',
    permKey: 'position',
  },
  // Thứ tự 4 tile khớp sidebar mới: Purchase Contract → Inbound Logistics
  // → Sale Contract → Outbound Logistics (2026-07-02).
  {
    title: 'Purchase Contract',
    subtitle: 'Purchases, NPE, PTBF, amendments',
    path: '/purchase/contract/list',
    icon: Description,
    color: '#a14b2a',
    permKey: 'purchase',
  },
  {
    title: 'Inbound Logistics',
    subtitle: 'Purchase, pay data, invoices',
    path: '/purchase/control/purchase-contract',
    icon: FactCheck,
    color: '#da833c',
    permKey: 'procurement',
  },
  {
    title: 'Sales Contract',
    subtitle: 'Contracts, consignment, amendment',
    path: '/sales/contract/list',
    icon: Assignment,
    color: '#145a59',
    permKey: 'saleContract',
  },
  {
    title: 'Outbound Logistics',
    subtitle: 'Local sales and delivery workflow',
    path: '/sales/control/sales-contract',
    icon: Store,
    color: '#0b3633',
    permKey: 'saleControl',
  },
  {
    title: 'Futures',
    subtitle: 'Orders, allocation, position, PnL',
    path: '/futures/summary',
    icon: ShowChart,
    color: '#bc2434',
    permKey: 'futures',
  },
  {
    title: 'Factory control',
    subtitle: 'Factory overview and summary',
    path: '/factory/summary',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factory',
  },
  {
    title: 'Bảo Lộc factory',
    subtitle: 'Bao Loc factory dashboard',
    path: '/baoloc',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryBl',
  },
  {
    title: 'Nhơn Trạch factory',
    subtitle: 'Nhon Trach factory dashboard',
    path: '/nhontrach',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryNt',
  },
  {
    title: 'An Phước factory',
    subtitle: 'An Phuoc factory dashboard',
    path: '/nguyencuong',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryNc',
  },
  {
    title: 'Nông Sản factory',
    subtitle: 'Nong San factory dashboard',
    path: '/nongsan',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryNs',
  },
  {
    title: 'Pacorini factory',
    subtitle: 'Pacorini factory dashboard',
    path: '/pacorini',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryPaco',
  },
  {
    title: 'Matching P-S',
    subtitle: 'Match - Instruct, Allocating, S/P Contract',
    path: '/matching',
    icon: CompareArrows,
    color: '#2e6e6c',
    permKey: 'matchingPs',
  },
  {
    title: 'Quality',
    subtitle: 'Overview, PSS label, Nestle sample',
    path: '/quality/overview',
    icon: Science,
    color: '#5b8f3a',
    permKey: 'quality',
  },
  {
    title: 'Virtual Warehouse',
    subtitle: 'Receipts, notes, quality discount',
    path: '/virtual-warehouse/received',
    icon: Warehouse,
    color: '#145a59',
    permKey: 'virtualWarehouse',
  },
  {
    title: 'Master Data',
    subtitle: 'Vendors, items, packages, references',
    path: '/master-data',
    icon: Storage,
    color: '#3d5a80',
    permKey: 'masterData',
  },
  {
    title: 'Setting',
    subtitle: 'Roles, users, log history',
    path: '/settings/roles',
    icon: Settings,
    color: '#5a5a5a',
    permKey: 'setting',
  },
]

const statusCards = [
  { label: 'Today Workflow', value: 'Ready', helper: 'Open module shortcuts from one place', progress: 88 },
  { label: 'ERP Coverage', value: '17 modules', helper: 'Sales, Purchase, Futures, Quality and more', progress: 72 },
  { label: 'Data Access', value: 'Secured', helper: 'Role based access is applied after login', progress: 95 },
]

export default function HomePage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const token = useAppSelector((state) => state.auth.token)
  const userFromAuth = useAppSelector((state) => state.auth.user)
  const permissions = useAppSelector((state) => state.auth.permissions)
  const { data: user, isLoading, error } = useGetMeQuery(undefined, { skip: !token })

  const displayUser = user ?? userFromAuth

  // Hiện đủ tile; NoGrant/View đổi style để phân biệt (không ẩn như trước).
  const visibleQuickLinks = useMemo(() => {
    const hasPerms = Object.keys(permissions).length > 0
    return quickLinks.map((link) => ({
      ...link,
      level: hasPerms ? permissions[link.permKey] ?? 'Edit' : 'Edit',
    }))
  }, [permissions])
  const greetingName = displayUser?.full_name || displayUser?.username || 'You'
  const today = useMemo(
    () =>
      new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  if (isLoading && !displayUser) {
    return <PageDataState isLoading />
  }
  if (error && !displayUser) {
    return <PageDataState error="Failed to load user information" />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Banner — style theo plan/ERP CTRM Home.dc.html hướng 1a (Dark Forest Teal). */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '14px',
          px: { xs: 2.5, md: 3.5 },
          py: { xs: 2.25, md: 2.75 },
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #0b3633 0%, #145a59 58%, #0f2b28 100%)'
              : 'linear-gradient(115deg, #f8ede0 0%, #eef6f2 55%, #d6ece5 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 200,
            height: 200,
            borderRadius: '50%',
            border: '1.5px solid rgba(13,120,98,0.13)',
            backgroundColor: 'rgba(13,120,98,0.05)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 55,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 130,
            height: 130,
            borderRadius: '50%',
            border: '1.5px solid rgba(13,120,98,0.09)',
            backgroundColor: 'rgba(13,120,98,0.03)',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            size="small"
            icon={<Dashboard sx={{ fontSize: 14 }} />}
            label={today}
            sx={{
              mb: 1.25,
              fontSize: '0.71rem',
              fontWeight: 600,
              color: '#555',
              bgcolor: 'rgba(255,255,255,0.88)',
              border: 'none',
            }}
          />
          <Typography fontWeight={700} sx={{ fontSize: 20, color: theme.palette.mode === 'dark' ? '#e8f2ee' : '#0d3b35' }}>
            Hello, {greetingName}
          </Typography>
          <Typography sx={{ mt: 0.5, maxWidth: 440, fontSize: 12, lineHeight: 1.6, color: theme.palette.mode === 'dark' ? 'rgba(232,242,238,0.75)' : '#5a6c66' }}>
            ERP Center for Purchase, Sales, Futures, Factory, Logistics and more. Select a module to
            continue your work faster.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        {statusCards.map((card) => (
          <Grid item xs={12} md={4} key={card.label}>
            <Card
              sx={{
                height: '100%',
                borderRadius: '11px',
                border: '1px solid #e6e4de',
                boxShadow: 'none',
                backgroundColor: 'background.paper',
              }}
            >
              <CardContent sx={{ p: '15px 18px', '&:last-child': { pb: '15px' } }}>
                <Typography sx={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  {card.label}
                </Typography>
                <Typography fontWeight={700} sx={{ fontSize: 18, mt: 0.25 }}>
                  {card.value}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#bbb', mt: 0.25, mb: 1.5 }}>
                  {card.helper}
                </Typography>
                <Box sx={{ height: 4, borderRadius: '2px', backgroundColor: '#ede9e2' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${card.progress}%`,
                      borderRadius: '2px',
                      background: 'linear-gradient(90deg, #0d3b35, #2da090)',
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography fontWeight={600} sx={{ fontSize: 13.5 }}>
            Quick Access
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#aaa' }}>
            Main ERP modules
          </Typography>
        </Stack>

        <Grid container spacing={1}>
          {visibleQuickLinks.map((item) => {
            const isView = item.level === 'View'
            const isNoGrant = item.level === 'NoGrant'
            // Mirror sidebar: View → mắt teal, NoGrant → khoá đỏ trầm + card mờ không click.
            const Icon = isNoGrant ? LockOutlined : isView ? VisibilityOutlined : item.icon
            const iconColor = isNoGrant ? NOGRANT_COLOR : isView ? VIEW_COLOR : item.color
            return (
              <Grid item xs={12} sm={6} lg={4} key={item.title}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: isNoGrant ? 'not-allowed' : 'pointer',
                    borderRadius: '11px',
                    // Thang nổi bật: Edit wow nhất (gradient màu module + viền màu + hover mạnh)
                    // > View trắng phẳng dịu > NoGrant xám mờ viền đứt.
                    border: isNoGrant
                      ? '1px dashed #ddd8cf'
                      : isView
                      ? '1px solid #e6e4de'
                      : `1px solid ${alpha(EDIT_ACCENT, 0.3)}`,
                    boxShadow: isNoGrant || isView ? 'none' : `0 2px 10px -6px ${alpha(EDIT_ACCENT, 0.45)}`,
                    // Nền phẳng: Edit + View đều trắng, chỉ phân biệt bằng viền/shadow/icon.
                    background: isNoGrant ? '#f6f4ef' : '#ffffff',
                    opacity: isNoGrant ? 0.62 : isView ? 0.82 : 1,
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    ...(isNoGrant
                      ? {}
                      : isView
                      ? {
                          '&:hover': {
                            boxShadow: '0 3px 10px rgba(0,0,0,0.05)',
                          },
                        }
                      : {
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 20px -8px ${alpha(EDIT_ACCENT, 0.55)}`,
                            borderColor: alpha(EDIT_ACCENT, 0.5),
                          },
                        }),
                  }}
                  onClick={() => {
                    if (isNoGrant) return
                    navigate(item.path)
                  }}
                >
                  <CardContent sx={{ p: '12px 14px', '&:last-child': { pb: '12px' } }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          color: iconColor,
                          backgroundColor: alpha(iconColor, 0.1),
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 17 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 12, lineHeight: 1.3, color: isNoGrant ? '#8b8578' : isView ? '#57635f' : undefined }}>
                          {item.title}
                        </Typography>
                        <Typography noWrap sx={{ mt: '1px', fontSize: 10, color: '#bbb' }}>
                          {item.subtitle}
                        </Typography>
                      </Box>
                      {isView && (
                        <Chip
                          label="View"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 9,
                            fontWeight: 700,
                            color: VIEW_COLOR,
                            bgcolor: alpha(VIEW_COLOR, 0.1),
                          }}
                        />
                      )}
                      {isNoGrant && (
                        <Chip
                          label="No access"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 9,
                            fontWeight: 700,
                            color: NOGRANT_COLOR,
                            bgcolor: alpha(NOGRANT_COLOR, 0.08),
                          }}
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Box>
    </Box>
  )
}
